import React from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import {
    Badge,
    Box,
    CircularProgress,
    Fab,
    LinearProgress,
    Paper,
    Typography
} from '@mui/material';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { apiFetch } from '../apiClient';
import { logDebug, logError, logInfo, logWarn } from '../logging/loggingService';
import { useAuth } from '../components/auth/AuthContext';
import { API_BASE_URL } from '../utils/apiBaseUrl';
import ChatWindow from './ChatWindow';
import ChatInput from './ChatInput';
import QuickActions from './QuickActions';
import { initialMessageState, mapEventToMessage, messageReducer } from './messageReducer';
import { classifyIntent } from './nlu';

const SESSION_STORAGE_KEY = 'chatbotSessionId';
const EVENTS_ENDPOINT = '/ws/events';

const buildSocketUrl = () => (EVENTS_ENDPOINT.startsWith('http')
    ? EVENTS_ENDPOINT
    : `${API_BASE_URL}${EVENTS_ENDPOINT}`);

const AUTH_ERROR_PATTERN = /(401|403|unauthori[sz]ed|forbidden)/i;

const isAuthErrorFrame = (frame) => {
    if (!frame) {
        return false;
    }
    const message = frame?.headers?.message ?? '';
    const body = frame?.body ?? '';
    return AUTH_ERROR_PATTERN.test(message) || AUTH_ERROR_PATTERN.test(body);
};

const isAuthCloseEvent = (event) => {
    if (!event) {
        return false;
    }
    if (event.code === 4001 || event.code === 4003) {
        return true;
    }
    const reason = event.reason ?? '';
    return AUTH_ERROR_PATTERN.test(reason);
};

const readStoredToken = () => {
    try {
        return localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || null;
    } catch {
        return null;
    }
};

const ensureSessionId = () => {
    try {
        const existing = localStorage.getItem(SESSION_STORAGE_KEY) || sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (existing) {
            return existing;
        }
        const generated = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `session-${Date.now()}`;
        sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
        return generated;
    } catch (error) {
        return `session-${Date.now()}`;
    }
};

export default function ChatbotWidget() {
    const { isAuthenticated } = useAuth();
    const [isOpen, setIsOpen] = React.useState(false);
    const [sessionId, setSessionId] = React.useState(() => ensureSessionId());
    const sessionRef = React.useRef(sessionId);
    const [messageState, dispatchMessages] = React.useReducer(messageReducer, initialMessageState);
    const messages = messageState.messages;
    const [inputValue, setInputValue] = React.useState('');
    const [isSending, setIsSending] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState(null);
    const [hasUnread, setHasUnread] = React.useState(false);
    const stompClientRef = React.useRef(null);
    const subscriptionRef = React.useRef(null);
    const openRef = React.useRef(isOpen);

    React.useEffect(() => {
        sessionRef.current = sessionId;
        try {
            localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
        } catch {}
    }, [sessionId]);

    React.useEffect(() => {
        openRef.current = isOpen;
        if (isOpen) {
            setHasUnread(false);
        }
    }, [isOpen]);

    React.useEffect(() => {
        if (!sessionId || !isAuthenticated || !isOpen) {
            return;
        }
        let active = true;
        const fetchSnapshot = async () => {
            try {
                const response = await apiFetch(`/api/chatbot/session/${sessionId}`);
                const data = await response.json();
                if (!active) {
                    return;
                }
                if (Array.isArray(data?.transcript)) {
                    dispatchMessages({ type: 'replace', messages: data.transcript });
                }
            } catch (error) {
                if (error?.response?.status === 404) {
                    return;
                }
            }
        };
        fetchSnapshot();
        const interval = setInterval(fetchSnapshot, 10000);
        return () => {
            active = false;
            clearInterval(interval);
        };
    }, [dispatchMessages, sessionId, isAuthenticated, isOpen]);

    React.useEffect(() => {
        if (!isAuthenticated) {
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
            }
            if (stompClientRef.current) {
                stompClientRef.current.deactivate();
                stompClientRef.current = null;
            }
            return;
        }

        const client = new Client({
            webSocketFactory: () => new SockJS(buildSocketUrl()),
            reconnectDelay: 5000,
        });

        const updateConnectHeaders = () => {
            const token = readStoredToken();
            client.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        };

        updateConnectHeaders();
        client.beforeConnect = updateConnectHeaders;

        const reinitializeClient = () => {
            if (stompClientRef.current !== client || !isAuthenticated) {
                return;
            }
            updateConnectHeaders();
            client.deactivate().finally(() => {
                if (stompClientRef.current === client && isAuthenticated) {
                    client.activate();
                }
            });
        };

        client.onConnect = () => {
            logInfo('chatbot.websocket.connected', { component: 'ChatbotWidget' });
            subscriptionRef.current = client.subscribe('/topic/chatbot-events', (frame) => {
                try {
                    const payload = JSON.parse(frame.body ?? '{}');
                    const message = mapEventToMessage(payload);
                    if (message) {
                        dispatchMessages({ type: 'append', message });
                        if (!openRef.current) {
                            setHasUnread(true);
                        }
                    }
                } catch (error) {
                    logError('chatbot.websocket.parse_error', error, {
                        component: 'ChatbotWidget',
                        body: frame.body,
                    });
                }
            });
        };

        client.onDisconnect = () => {
            logInfo('chatbot.websocket.disconnected', { component: 'ChatbotWidget' });
        };

        client.onStompError = (frame) => {
            logWarn('chatbot.websocket.stomp_error', {
                component: 'ChatbotWidget',
                headers: frame.headers,
                body: frame.body,
            });
            if (isAuthErrorFrame(frame)) {
                reinitializeClient();
            }
        };

        client.onWebSocketError = (event) => {
            logError('chatbot.websocket.transport_error', {
                component: 'ChatbotWidget',
                message: event?.message,
            });
        };

        client.onWebSocketClose = (event) => {
            if (isAuthCloseEvent(event)) {
                reinitializeClient();
            }
        };

        client.debug = (message) => {
            logDebug('chatbot.websocket.debug', { component: 'ChatbotWidget', message });
        };

        client.activate();
        stompClientRef.current = client;

        return () => {
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
            }
            client.deactivate();
            stompClientRef.current = null;
        };
    }, [dispatchMessages, isAuthenticated]);

    const appendUserMessage = React.useCallback((text) => {
        dispatchMessages({ type: 'append', message: { role: 'user', type: 'text', text } });
    }, [dispatchMessages]);

    const sendToBackend = React.useCallback(async (payload) => {
        setIsSending(true);
        setErrorMessage(null);
        try {
            const response = await apiFetch('/api/chatbot/webhook', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (data?.sessionId && data.sessionId !== sessionRef.current) {
                setSessionId(data.sessionId);
            }
            if (Array.isArray(data?.messages)) {
                dispatchMessages({ type: 'appendMany', messages: data.messages });
            }
        } catch (error) {
            setErrorMessage('Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.');
            logError('chatbot.webhook_failed', error);
        } finally {
            setIsSending(false);
        }
    }, [dispatchMessages]);

    const handleSubmit = React.useCallback((text, providedIntent = null, providedSlots = null) => {
        if (!isAuthenticated) {
            setErrorMessage('Bitte melde dich an, um den Chatbot zu nutzen.');
            return;
        }
        const trimmed = text.trim();
        if (!trimmed) {
            return;
        }
        appendUserMessage(trimmed);
        setInputValue('');

        let intent = providedIntent;
        let slots = providedSlots || {};
        let confidence = 1.0;

        if (!intent) {
            const classification = classifyIntent(trimmed);
            intent = classification.intent;
            slots = classification.slots;
            confidence = classification.confidence;
        }

        const payload = {
            sessionId: sessionRef.current,
            intent,
            text: trimmed,
            slots,
            confidence,
        };
        sendToBackend(payload);
    }, [appendUserMessage, sendToBackend, isAuthenticated]);

    const handleAction = React.useCallback((action) => {
        if (!action) {
            return;
        }
        handleSubmit(action.message ?? action.label, action.intent, action.slots ?? {});
    }, [handleSubmit]);

    const toggleOpen = () => setIsOpen((open) => !open);
    const hasHistory = messages.length > 0;
    const showBadge = !isOpen && (hasUnread || hasHistory);

    return (
        <>
            <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
                <Badge color="secondary" variant="dot" invisible={!showBadge} overlap="circular">
                    <Fab color={isOpen ? 'default' : 'primary'} onClick={toggleOpen} aria-label="Chatbot öffnen oder schließen">
                        {isOpen ? <CloseRoundedIcon /> : <ChatRoundedIcon />}
                    </Fab>
                </Badge>
            </Box>
            {isOpen && (
                <Paper
                    elevation={8}
                    sx={{
                        position: 'fixed',
                        bottom: 96,
                        right: 24,
                        width: { xs: '90vw', sm: 360 },
                        height: 520,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 3,
                        overflow: 'hidden',
                        zIndex: 1300,
                    }}
                >
                    <Box sx={{ px: 2, py: 1.5, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                        <Typography variant="subtitle1" fontWeight={600}>Buchhaltungs-Chatbot</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Verbinde dich mit Rechnungs- und Reporting-Services in Echtzeit.
                        </Typography>
                    </Box>
                    <QuickActions onAction={handleAction} disabled={isSending || !isAuthenticated} />
                    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <ChatWindow messages={messages} />
                        {isSending && (
                            <Box sx={{ position: 'absolute', top: 8, right: 16 }}>
                                <CircularProgress size={16} thickness={5} />
                            </Box>
                        )}
                    </Box>
                    {errorMessage && (
                        <Typography variant="caption" color="error" sx={{ px: 2 }}>
                            {errorMessage}
                        </Typography>
                    )}
                    <ChatInput
                        value={inputValue}
                        onChange={setInputValue}
                        onSubmit={(value) => handleSubmit(value)}
                        disabled={isSending || !isAuthenticated}
                    />
                    {isSending && <LinearProgress color="primary" />}
                </Paper>
            )}
        </>
    );
}
